import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/tanstackstart-react'
import FocusLock from 'react-focus-lock'
import { Trash2 as TrashIcon } from 'lucide-react'
import type { DeviceResponse } from '../lib/api'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Checkbox } from './ui/checkbox'

const DAYS = [
  { value: 0, label: 'Sun', short: 'S' },
  { value: 1, label: 'Mon', short: 'M' },
  { value: 2, label: 'Tue', short: 'T' },
  { value: 3, label: 'Wed', short: 'W' },
  { value: 4, label: 'Thu', short: 'T' },
  { value: 5, label: 'Fri', short: 'F' },
  { value: 6, label: 'Sat', short: 'S' },
]

const TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern (ET)' },
  { value: 'America/Chicago', label: 'Central (CT)' },
  { value: 'America/Denver', label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix', label: 'Arizona' },
  { value: 'America/Anchorage', label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii (HT)' },
  { value: 'Europe/London', label: 'London (GMT)' },
  { value: 'Europe/Paris', label: 'Europe (CET)' },
  { value: 'Asia/Tokyo', label: 'Japan (JST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST)' },
  { value: 'UTC', label: 'UTC' },
]

/** Minimal schedule shape that both ScheduleResponse and SchoolSchedule satisfy */
interface ScheduleData {
  name: string
  policyId: string
  timezone: string
  blocks: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
  devices?: Array<{ deviceId: string }>
}

/** Minimal policy shape that both PolicyResponse and SchoolPolicy satisfy */
interface PolicyData {
  id: string
  name: string
}

interface FieldErrors {
  name?: string
  policyId?: string
  days?: string
  devices?: string
  time?: string
}

interface SharedProps {
  isOpen: boolean
  onClose: () => void
  onDelete?: () => Promise<void>
  schedule?: ScheduleData | null
  policies: PolicyData[]
  initialDayOfWeek?: number
  initialStartTime?: string
  initialEndTime?: string
}

interface FamilyProps extends SharedProps {
  variant: 'family'
  devices: DeviceResponse[]
  onSave: (data: {
    name: string
    policyId: string
    timezone: string
    blocks: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
    deviceIds: string[]
  }) => Promise<void>
}

interface SchoolProps extends SharedProps {
  variant: 'school'
  onSave: (data: {
    name: string
    policyId: string
    timezone: string
    blocks: Array<{ dayOfWeek: number; startTime: string; endTime: string }>
  }) => Promise<void>
}

type ScheduleModalProps = FamilyProps | SchoolProps

const SCHEDULE_TIME_PATTERN = /^([01]?\d|2[0-3]):[0-5]\d$/

function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'America/New_York'
  }
}

function timeInputValue(time: string): string {
  return SCHEDULE_TIME_PATTERN.test(time) ? time : ''
}

export default function ScheduleModal(props: ScheduleModalProps) {
  const {
    isOpen,
    onClose,
    onDelete,
    schedule,
    policies,
    variant,
    initialDayOfWeek,
    initialStartTime,
    initialEndTime,
  } = props

  const [name, setName] = useState('')
  const [policyId, setPolicyId] = useState('')
  const [timezone, setTimezone] = useState(getBrowserTimezone())
  const [selectedDays, setSelectedDays] = useState<number[]>([])
  const [startTime, setStartTime] = useState('09:00')
  const [endTime, setEndTime] = useState('17:00')
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<string[]>([])
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({})

  const devices = variant === 'family' ? props.devices : []

  useEffect(() => {
    if (isOpen) {
      if (schedule) {
        setName(schedule.name)
        setPolicyId(schedule.policyId)
        setTimezone(schedule.timezone)
        const days = [...new Set(schedule.blocks.map((b) => b.dayOfWeek))]
        setSelectedDays(days)
        if (schedule.blocks.length > 0) {
          const block = schedule.blocks[0]
          setStartTime(timeInputValue(block.startTime))
          setEndTime(timeInputValue(block.endTime))
        }
        if (schedule.devices) {
          setSelectedDeviceIds(schedule.devices.map((d) => d.deviceId))
        }
      } else {
        setName('')
        setPolicyId(policies[0]?.id || '')
        setTimezone(getBrowserTimezone())
        setSelectedDays(initialDayOfWeek !== undefined ? [initialDayOfWeek] : [])
        setStartTime(timeInputValue(initialStartTime || '09:00'))
        setEndTime(timeInputValue(initialEndTime || '17:00'))
        setSelectedDeviceIds([])
      }
      setError(null)
      setFieldErrors({})
      setShowDeleteConfirm(false)
    }
  }, [isOpen, schedule, policies, initialDayOfWeek, initialStartTime, initialEndTime])

  const handleDayToggle = (day: number) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    )
  }

  const handleDeviceToggle = (deviceId: string) => {
    setSelectedDeviceIds((prev) =>
      prev.includes(deviceId) ? prev.filter((id) => id !== deviceId) : [...prev, deviceId]
    )
  }

  const handleSelectAllDevices = () => {
    if (selectedDeviceIds.length === devices.length) {
      setSelectedDeviceIds([])
    } else {
      setSelectedDeviceIds(devices.map((d) => d.id))
    }
  }

  const handleSave = async () => {
    setError(null)
    const errors: FieldErrors = {}

    if (!name.trim()) errors.name = 'Please enter a schedule name'
    if (!policyId) errors.policyId = 'Please select a rule'
    if (selectedDays.length === 0) errors.days = 'Please select at least one day'
    if (variant === 'family' && selectedDeviceIds.length === 0) errors.devices = 'Please select at least one device'
    if (!SCHEDULE_TIME_PATTERN.test(startTime) || !SCHEDULE_TIME_PATTERN.test(endTime)) {
      errors.time = 'Please enter valid start and end times'
    } else if (startTime === endTime) {
      errors.time = 'Start and end time cannot be the same'
    }

    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSaving(true)
    try {
      const blocks = selectedDays.map((dayOfWeek) => ({ dayOfWeek, startTime, endTime }))
      if (variant === 'family') {
        await props.onSave({ name: name.trim(), policyId, timezone, blocks, deviceIds: selectedDeviceIds })
      } else {
        await props.onSave({ name: name.trim(), policyId, timezone, blocks })
      }
      onClose()
    } catch (err) {
      Sentry.captureException(err)
      setError(err instanceof Error ? err.message : 'Failed to save schedule')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!onDelete) return
    setDeleting(true)
    try {
      await onDelete()
      setShowDeleteConfirm(false)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete schedule')
    } finally {
      setDeleting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4">
      <FocusLock returnFocus>
      <div className="bg-white w-full sm:rounded-2xl sm:max-w-xl sm:w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col rounded-t-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:px-6 sm:py-4 border-b border-gray-100 shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">
            {schedule ? 'Edit Schedule' : 'New Schedule'}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-1.5 text-sm bg-brand text-white rounded-lg hover:bg-brand-hover active:bg-brand-hover transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : schedule ? 'Save' : 'Create'}
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Name */}
          <div className="space-y-1.5">
            <Label>Name</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={variant === 'school' ? 'e.g., School Hours, After School' : 'e.g., Bedtime, School Hours'}
            />
            {fieldErrors.name && (
              <p className="text-xs text-red-600">{fieldErrors.name}</p>
            )}
          </div>

          {/* Rule (Policy) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Rule</label>
            <select
              value={policyId}
              onChange={(e) => setPolicyId(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base sm:text-sm focus-visible:ring-2 focus-visible:ring-brand/50 outline-none bg-white"
            >
              <option value="">Select a rule...</option>
              {policies.map((policy) => (
                <option key={policy.id} value={policy.id}>{policy.name}</option>
              ))}
            </select>
            {fieldErrors.policyId && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.policyId}</p>
            )}
          </div>

          {/* Timezone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-base sm:text-sm focus-visible:ring-2 focus-visible:ring-brand/50 outline-none bg-white"
            >
              {TIMEZONES.map((tz) => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          {/* Days */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Days</label>
            <div className="flex gap-1.5 sm:gap-2">
              {DAYS.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => handleDayToggle(day.value)}
                  className={`flex-1 py-2 sm:py-1.5 text-sm font-medium rounded-lg transition-colors ${
                    selectedDays.includes(day.value)
                      ? 'bg-brand text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200 active:bg-gray-200'
                  }`}
                >
                  <span className="sm:hidden">{day.short}</span>
                  <span className="hidden sm:inline">{day.label}</span>
                </button>
              ))}
            </div>
            {fieldErrors.days && (
              <p className="text-xs text-red-600 mt-1">{fieldErrors.days}</p>
            )}
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Start</Label>
              <Input
                type="time"
                value={timeInputValue(startTime)}
                onChange={(e) => setStartTime(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>End</Label>
              <Input
                type="time"
                value={timeInputValue(endTime)}
                onChange={(e) => setEndTime(e.target.value)}
              />
            </div>
            {fieldErrors.time && (
              <p className="text-xs text-red-600 col-span-2">{fieldErrors.time}</p>
            )}
          </div>

          {/* Devices — family variant only */}
          {variant === 'family' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Devices</label>
                {devices.length > 0 && (
                  <button
                    type="button"
                    onClick={handleSelectAllDevices}
                    className="text-xs text-brand active:opacity-70"
                  >
                    {selectedDeviceIds.length === devices.length ? 'Deselect All' : 'Select All'}
                  </button>
                )}
              </div>
              {devices.length === 0 ? (
                <p className="text-sm text-gray-500 bg-gray-50 rounded-lg p-3">No devices available.</p>
              ) : (
                <div className="space-y-1 max-h-32 sm:max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-1.5">
                  {devices.map((device) => (
                    <label
                      key={device.id}
                      className="flex items-center gap-3 p-2.5 sm:p-2 rounded-lg hover:bg-gray-100 active:bg-gray-100 transition-colors cursor-pointer"
                    >
                      <Checkbox
                        id={`device-${device.id}`}
                        checked={selectedDeviceIds.includes(device.id)}
                        onCheckedChange={() => handleDeviceToggle(device.id)}
                        aria-label={device.name || 'Unnamed Device'}
                      />
                      <span className="text-sm text-gray-700 truncate flex-1">
                        {device.name || 'Unnamed Device'}
                      </span>
                      <span className="text-xs text-gray-400 shrink-0">{device.platform}</span>
                    </label>
                  ))}
                </div>
              )}
              {fieldErrors.devices && (
                <p className="text-xs text-red-600 mt-1">{fieldErrors.devices}</p>
              )}
            </div>
          )}
        </div>

        {/* Footer - Delete button */}
        {schedule && onDelete && (
          <div className="shrink-0 p-4 border-t border-gray-100 bg-white safe-area-inset-bottom">
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={deleting}
              className="flex items-center gap-2 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 active:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
            >
              <TrashIcon size={16} />
              <span>{deleting ? 'Deleting...' : 'Delete Schedule'}</span>
            </button>
          </div>
        )}
      </div>

      </FocusLock>

      {/* Inline confirm dialog */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Schedule</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this schedule? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
